import { Choreographer } from './core/choreography'
import { coarsePointer, finePointer, isLowPowerDevice, prefersReducedMotion, safeGet } from './core/env'
import { damp } from './core/math'
import { isInViewport, observeReveals, replayReveal, startReveals } from './core/reveal'
import { Scroller } from './core/scroll'
import { initScrollStop, type ScrollStopController } from './core/scrollStop'
import { store, type AppState, type Audience } from './core/store'
import { GyroParallaxManager } from './core/gyro'
import { cancelTyping, splitWords, typeInto, typewrite } from './core/text'
import { htmlLang, intlLocale, translate } from './i18n'
import { createStage, supportsOffscreenStage } from './scene/createStage'
import type { StageController } from './scene/types'
import { Agent } from './ui/agent'
import { initContactForm } from './ui/contact'
import { $, $$, pad } from './ui/dom'
import { CyberHint, fitWordmark, initPointerEffects, runPreloader, startClock } from './ui/extras'
import { Header } from './ui/header'
import type { LiveChat } from './ui/liveChat'
import { hasStoredChatSession } from './core/chatSession'
import { layoutViewport } from './core/viewport'
import type { CookieConsentManager } from './ui/cookieConsent'
import { renderChecklist, renderMarquee, renderServices, renderSteps } from './ui/sections'
import { CaseViewer, renderWork, showMoreWork } from './ui/work'
import { homePath, parseRoute } from './core/routes'

const THEME_COLOR = { dark: '#0a0b0c', light: '#f2f0eb', cyber: '#020503' } as const

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void> }
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))
const nextFrames = (n: number) =>
  new Promise<void>((resolve) => {
    const step = () => (--n <= 0 ? resolve() : requestAnimationFrame(step))
    requestAnimationFrame(step)
  })

/** Wires state, copy, DOM sections and the WebGL stage into one frame loop. */
export class App {
  private readonly reduced = prefersReducedMotion()
  private readonly root = document.documentElement
  private scroller = new Scroller()
  private header = new Header(this.scroller, (id) => this.scrollStop?.getStop(id))
  private cases = new CaseViewer(this.scroller, (index) => showMoreWork($('#work-grid'), index + 1))
  private hint: CyberHint
  private agent: Agent
  private choreo: Choreographer
  private stage: StageController | null = null

  private veil = $('#stage-veil')
  private veilValue = -1
  private stepsWrap = $('#steps-wrap')
  private readoutFloor = $('#readout-floor')
  private floorCount = 18
  private activeStep = -2
  private lastFloor = -1
  private lastProcess = -1
  private pointer = { x: 0, y: 0, tx: 0, ty: 0 }
  private themeOrigin: { x: number; y: number } | null = null
  /** Cyber typewriter queue: start time + running order for staggered typing. */
  private typeQueue = { base: 0, order: 0 }
  private refreshClock: () => void = () => {}
  private size = { w: 0, h: 0 }
  private last = performance.now()
  private raf = 0
  private scrollStop: ScrollStopController | null = null
  private liveChat: LiveChat | null = null
  private liveChatReady: Promise<LiveChat> | null = null
  private cookieManager: CookieConsentManager | null = null
  private gyro: GyroParallaxManager | null = null
  private lastPulseTime = 0
  private lastScrolledY = 0

  private triggerInteractionPulse(fromScroll = false): void {
    const now = performance.now()
    const cooldown = fromScroll ? 700 : 350
    if (now - this.lastPulseTime < cooldown) return
    this.lastPulseTime = now
    this.stage?.pulse()
  }

  constructor() {
    this.hint = new CyberHint(
      () => translate(store.get().lang, 'ui.cyberHint'),
      (visible) => (this.header.pinned = visible),
    )
    this.choreo = new Choreographer($$('[data-section]'), this.stepsWrap)
    this.agent = new Agent(
      (section) => translate(store.get().lang, `agent.${section}`),
      {
        onNavigateContact: () => this.scrollToContact(),
        onOpenChat: () => this.openLiveChat(),
      },
    )
  }

  async start(): Promise<void> {
    const state = store.get()
    this.applyTheme(state)
    this.syncAudience(state.audience)
    this.header.syncLanguage(state.lang)
    this.syncHomeUrl()
    this.renderCopy(false)
    this.root.classList.add('copy-ready')
    const stageReady = this.initStage()
    this.bindControls()
    initContactForm()
    initPointerEffects()
    this.refreshClock = startClock(() => intlLocale[store.get().lang])
    $('#year').textContent = String(new Date().getFullYear())

    // Returning visitors keep replies and unread badges, so their chat connects on load.
    if (hasStoredChatSession()) void this.connectLiveChat()
    this.initLazyCookieConsent()

    this.gyro = new GyroParallaxManager()
    this.gyro.subscribe(({ roll, pitch }) => {
      this.stage?.setGyro?.(roll, pitch)
      this.agent.setGyro(roll, pitch)
    })

    store.subscribe((next, prev) => this.onStateChange(next, prev))
    const lenis = this.scroller.instance
    if (lenis) {
      let audienceCooldown = 0
      this.scrollStop = initScrollStop(lenis, {
        onBeforeStep: ({ currentStop, dir, y }) => {
          if (currentStop?.id === 'services' && Math.abs(y - currentStop.top) <= 50) {
            const now = performance.now()
            if (now - audienceCooldown < 220) return true
            const state = store.get()
            if (dir > 0 && state.audience === 'individuals') {
              audienceCooldown = now
              store.set({ audience: 'business' })
              const servicesTop = this.scrollStop?.getStop('services')
              if (typeof servicesTop === 'number') {
                this.scrollStop?.glideTo?.(servicesTop)
              }
              return true
            }
            if (dir < 0 && state.audience === 'business') {
              audienceCooldown = now
              store.set({ audience: 'individuals' })
              const servicesTop = this.scrollStop?.getStop('services')
              if (typeof servicesTop === 'number') {
                this.scrollStop?.glideTo?.(servicesTop)
              }
              return true
            }
          }
          return false
        },
      })
    }
    this.observeLayout()
    this.loop()

    // Only the hero faces (Inter Tight 500 for the H1, 400 for the lead). `document.fonts.ready` would also wait
    // for every other face the page has requested, which is what kept the hero behind the preloader.
    const fonts = document.fonts?.load
      ? Promise.all([document.fonts.load('500 16px "Inter Tight"'), document.fonts.load('400 16px "Inter Tight"')]).catch(() => undefined)
      : Promise.resolve()
    // The preloader only waits for fonts and the first frames, so the hero text lifts into view
    // while the 3D scene is still building. The scene fades in behind it once it is ready.
    await runPreloader(Promise.race([Promise.all([fonts, nextFrames(3)]), wait(2000)]))
    // /work/<id>/ (and the /me/, /ru/ variants) open the case sheet over the home page, without waiting for the 3D scene.
    this.cases.syncFromLocation()
    await stageReady
    void this.stage?.compileShaders()

    fitWordmark()
    this.choreo.measure()
    this.scrollStop?.measure()
    startReveals()
    this.stage?.startIntro()
    this.agent.start()
    if (!store.get().cyber) this.hint.schedule()

    const hashTarget = location.hash ? document.getElementById(location.hash.slice(1)) : null
    if (hashTarget && hashTarget.id !== 'home') {
      const top = this.scrollStop?.getStop(hashTarget.id) ?? hashTarget
      this.scroller.to(top)
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.raf)
    this.stage?.dispose()
    this.scrollStop?.destroy()
    this.liveChat?.dispose()
  }

  // ───────────────────────────── State ─────────────────────────────

  private onStateChange(next: AppState, prev: AppState): void {
    if (next.lang !== prev.lang) {
      this.header.syncLanguage(next.lang)
      this.renderCopy(true)
      this.cases.refresh()
      this.syncHomeUrl()
      this.agent.refresh()
      this.liveChat?.refreshTexts()
      this.cookieManager?.refreshTexts()
      this.refreshClock()
      requestAnimationFrame(() => {
        fitWordmark()
        this.choreo.measure()
      })
    }
    if (next.cyber !== prev.cyber) this.switchCyber(next)
    else if (next.theme !== prev.theme) this.switchTheme(next)
    if (next.audience !== prev.audience) this.switchAudience(next)
  }

  private scrollToContact(): void {
    const contactSection = $('#contact')
    const form = $('#contact-form')
    const nameInput = $('#f-name') as HTMLInputElement | null
    if (contactSection) {
      const top = this.scrollStop?.getStop('contact') ?? contactSection
      this.scroller.to(top)
      window.setTimeout(() => {
        form?.classList.add('is-highlighted')
        nameInput?.focus({ preventScroll: true })
        window.setTimeout(() => form?.classList.remove('is-highlighted'), 2200)
      }, 700)
    }
  }

  // New visitors make no chat requests until they open the chat. Returning visitors connect on load.
  private connectLiveChat(): Promise<LiveChat> {
    this.liveChatReady ??= import('./ui/liveChat').then(({ LiveChat }) => {
      this.liveChat = new LiveChat(this.agent)
      return this.liveChat
    })
    return this.liveChatReady
  }

  private async openLiveChat(): Promise<void> {
    const chat = await this.connectLiveChat()
    chat.open()
  }

  private initLazyCookieConsent(): void {
    const loadManager = async (showModalDirectly = false) => {
      if (!this.cookieManager) {
        const { CookieConsentManager } = await import('./ui/cookieConsent')
        this.cookieManager = new CookieConsentManager(this.agent)
      }
      if (showModalDirectly) {
        this.cookieManager.openPreferencesModal()
      } else {
        const hasConsent = safeGet('local', 'craft_cookie_consent_v1')
        if (!hasConsent) {
          this.cookieManager.showBanner()
        }
      }
    }

    const onFirstInteraction = () => {
      cleanup()
      window.setTimeout(() => {
        void loadManager(false)
      }, 500)
    }

    const cleanup = () => {
      window.removeEventListener('pointerdown', onFirstInteraction)
      window.removeEventListener('scroll', onFirstInteraction)
      window.removeEventListener('keydown', onFirstInteraction)
      window.removeEventListener('touchstart', onFirstInteraction)
      window.removeEventListener('wheel', onFirstInteraction)
    }

    const footerBtn = document.getElementById('cookie-settings-btn')
    footerBtn?.addEventListener('click', (e) => {
      e.preventDefault()
      cleanup()
      void loadManager(true)
    })

    const hasConsent = safeGet('local', 'craft_cookie_consent_v1')
    if (!hasConsent) {
      window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true })
      window.addEventListener('scroll', onFirstInteraction, { once: true, passive: true })
      window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true })
      window.addEventListener('touchstart', onFirstInteraction, { once: true, passive: true })
      window.addEventListener('wheel', onFirstInteraction, { once: true, passive: true })
    }
  }

  private applyTheme(state: AppState): void {
    this.root.dataset.theme = state.theme
    this.root.dataset.cyber = state.cyber ? 'on' : 'off'
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[state.cyber ? 'cyber' : state.theme])
    $$('.theme-toggle').forEach((button) => {
      button.setAttribute('aria-pressed', String(state.theme === 'light'))
      button.toggleAttribute('disabled', state.cyber)
    })
    $('#cyber-toggle').setAttribute('aria-checked', String(state.cyber))
  }

  /** Light/dark: circular reveal from the toggle via the View Transitions API. */
  private switchTheme(state: AppState): void {
    const doc = document as ViewTransitionDocument
    const apply = () => {
      this.applyTheme(state)
      this.stage?.setPalette(state.theme, 0)
      this.stage?.renderNow()
    }
    if (!doc.startViewTransition) {
      this.applyTheme(state)
      this.stage?.setPalette(state.theme, 0.5)
      return
    }
    const { x, y } = this.themeOrigin ?? { x: window.innerWidth - 120, y: 36 }
    const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
    doc
      .startViewTransition(apply)
      .ready.then(() => {
        this.root.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
          { duration: 900, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', pseudoElement: '::view-transition-new(root)' },
        )
      })
      .catch(() => {})
  }

  /**
   * Cyber: the screen shakes and tears (CSS + WebGL CRT spike), the palette flips mid-shake,
   * then the alternative copy types itself in with a block caret, as on the current site.
   */
  private switchCyber(state: AppState): void {
    const on = state.cyber
    const palette = on ? 'cyber' : state.theme
    if (on) this.hint.cancel()

    const commit = () => {
      this.applyTheme(state)
      this.stage?.setPalette(palette, 0.35)
      this.resetTypeQueue(on ? 420 : 0)
      this.refreshTexts(true)
      if (on) this.typeVisible(document)
      requestAnimationFrame(() => {
        fitWordmark()
        this.choreo.measure()
      })
    }

    this.root.classList.remove('cyber-boot', 'cyber-exit')
    void this.root.offsetWidth
    this.root.classList.add(on ? 'cyber-boot' : 'cyber-exit')
    if (on) typewrite($('#cyber-overlay-text'), translate(state.lang, 'ui.cyberBoot'), 16)
    this.stage?.glitchSpike()
    window.setTimeout(commit, on ? 300 : 140)
    window.setTimeout(() => this.root.classList.remove('cyber-boot', 'cyber-exit'), on ? 1000 : 420)
    if (!on) this.hint.schedule()
  }

  private resetTypeQueue(delay: number): void {
    this.typeQueue = { base: delay, order: 0 }
  }

  /** Staggered start times so visible lines type one after another, top to bottom. */
  private nextTypeDelay(): number {
    return this.typeQueue.base + Math.min(this.typeQueue.order++ * 150, 2000)
  }

  /** Type every visible list item (cards, steps, facts…) inside `root`. */
  private typeVisible(root: ParentNode): void {
    $$('[data-scr]', root)
      .filter((el) => isInViewport(el))
      .forEach((el) => typeInto(el, el.textContent ?? '', { delay: this.nextTypeDelay() }))
  }

  private syncAudience(audience: Audience): void {
    $('#audience').dataset.mode = audience
    $$<HTMLButtonElement>('[data-audience]').forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.audience === audience)),
    )
  }

  private switchAudience(state: AppState): void {
    this.syncAudience(state.audience)
    this.stage?.setAudience(state.audience)
    const grid = $('#services-grid')
    grid.style.minHeight = `${grid.offsetHeight}px`
    grid.classList.add('is-swapping')
    window.setTimeout(
      () => {
        renderServices(grid, state.lang, state.audience)
        grid.classList.remove('is-swapping')
        observeReveals(grid)
        if (state.cyber) {
          this.resetTypeQueue(0)
          this.typeVisible(grid)
        }
        window.setTimeout(() => (grid.style.minHeight = ''), 900)
        this.choreo.measure()
      },
      this.reduced ? 0 : 260,
    )
  }

  // ───────────────────────────── Copy ─────────────────────────────

  /**
   * Keeps the address bar on the current language's home URL (/, /me/, /ru/), so a shared link opens in the
   * language the sender sees. Case pages sync their own URL in `CaseViewer`.
   */
  private syncHomeUrl(): void {
    if (this.cases.isOpen || parseRoute(location.pathname).caseId) return
    const target = homePath(store.get().lang)
    if (location.pathname === target) return
    let search = location.search
    const params = new URLSearchParams(search)
    if (params.has('lang')) {
      params.delete('lang') // the path now says it
      search = params.size ? `?${params}` : ''
    }
    history.replaceState(history.state, '', `${target}${search}${location.hash}`)
  }

  private renderCopy(animate: boolean): void {
    const { lang, audience } = store.get()
    this.resetTypeQueue(0)
    this.refreshTexts(animate)

    renderMarquee($('#marquee'), lang)
    renderServices($('#services-grid'), lang, audience)
    renderWork($('#work-grid'), lang)
    renderSteps($('#steps'), lang)
    renderChecklist($('#checklist'), lang)
    this.activeStep = -2
    this.lastProcess = -1

    // Lists that are already on screen show immediately; the rest reveal on scroll.
    for (const id of ['#services-grid', '#work-grid', '#checklist']) {
      const container = $(id)
      if (animate) $$('[data-reveal]', container).filter((el) => isInViewport(el)).forEach((el) => el.classList.add('is-in'))
      observeReveals(container)
    }
    if (animate && store.get().cyber) this.typeVisible(document)

    this.root.lang = htmlLang[lang]
    // A prerendered /work/<id>/ page keeps its own title and description until the sheet takes over.
    if (!this.cases.isOpen) {
      document.title = translate(lang, 'meta.title')
      document.querySelector('meta[name="description"]')?.setAttribute('content', translate(lang, 'meta.description'))
    }
  }

  /** Update every [data-i18n] node and [data-i18n-attr] attribute. */
  private refreshTexts(animate: boolean): void {
    const { lang, cyber } = store.get()
    $$('[data-i18n]').forEach((el) => this.setText(el, translate(lang, el.dataset.i18n!, cyber), animate))
    $$('[data-i18n-attr]').forEach((el) => {
      for (const pair of el.dataset.i18nAttr!.split(';')) {
        const [attr, key] = pair.split(':')
        if (attr && key) el.setAttribute(attr.trim(), translate(lang, key.trim()))
      }
    })
  }

  private setText(el: HTMLElement, text: string, animate: boolean): void {
    const cyber = store.get().cyber
    const signature = `${cyber ? 'c' : 'n'}|${text}`
    if (el.dataset.rendered === signature) return
    el.dataset.rendered = signature

    if (cyber) {
      // Typewrite page copy that is on screen; chrome (nav, buttons in the header) just swaps.
      if (animate && isInViewport(el) && el.closest('main')) typeInto(el, text, { delay: this.nextTypeDelay() })
      else {
        cancelTyping(el)
        el.classList.remove('split')
        el.textContent = text
      }
      return
    }
    cancelTyping(el)
    if (el.hasAttribute('data-split')) {
      splitWords(el, text)
      if (animate && isInViewport(el)) replayReveal(el)
    } else {
      el.textContent = text
    }
  }

  // ───────────────────────────── Controls ─────────────────────────────

  private bindControls(): void {
    $$<HTMLButtonElement>('.theme-toggle').forEach((button) =>
      button.addEventListener('click', () => {
        if (store.get().cyber) return
        const r = button.getBoundingClientRect()
        this.themeOrigin = { x: r.left + r.width / 2, y: r.top + r.height / 2 }
        store.set({ theme: store.get().theme === 'dark' ? 'light' : 'dark' })
      }),
    )
    $('#cyber-toggle').addEventListener('click', () => store.set({ cyber: !store.get().cyber }))
    $$<HTMLButtonElement>('[data-audience]').forEach((button) =>
      button.addEventListener('click', () => store.set({ audience: button.dataset.audience as Audience })),
    )
    $('#work').addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (target.closest('[data-work-more-btn]')) {
        showMoreWork($('#work-grid'))?.querySelector<HTMLElement>('a')?.focus({ preventScroll: true })
        return
      }
      // Cards are real links (/work/<id>/); a plain click opens the sheet in place, modified clicks follow the link.
      const trigger = target.closest<HTMLElement>('[data-case]')
      if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      event.preventDefault()
      this.cases.open(trigger.dataset.case!, trigger)
    })
    $('#back-to-top').addEventListener('click', () => this.scroller.to(0))

    window.addEventListener('pointerdown', () => this.triggerInteractionPulse(false), { passive: true })
    window.addEventListener('scroll', () => this.triggerInteractionPulse(true), { passive: true })

    if (finePointer) {
      window.addEventListener(
        'pointermove',
        (event) => {
          this.pointer.tx = (event.clientX / window.innerWidth) * 2 - 1
          this.pointer.ty = -((event.clientY / window.innerHeight) * 2 - 1)
        },
        { passive: true },
      )
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cancelAnimationFrame(this.raf)
        this.raf = 0
      } else if (!this.raf) {
        this.last = performance.now()
        this.loop()
      }
    })
  }

  // ───────────────────────────── Stage ─────────────────────────────

  /** Three.js is loaded in a background Web Worker (OffscreenCanvas) or as a separate chunk on the main thread so it never blocks first paint / LCP / INP. */
  private async initStage(): Promise<void> {
    const canvas = $<HTMLCanvasElement>('#stage')
    if (location.search.includes('no-webgl=1') || /googlebot|bingbot|baiduspider|yandexbot/i.test(navigator.userAgent)) {
      this.disableStage()
      return
    }
    try {
      // Worker path: the scene is built off-thread, so start at once (the preloader waits on it).
      // Main-thread fallback: build the scene in idle time so it doesn't compete with first paint.
      if (!supportsOffscreenStage()) {
        await new Promise<void>((resolve) =>
          typeof requestIdleCallback === 'function' ? requestIdleCallback(() => resolve(), { timeout: 1200 }) : setTimeout(resolve, 200),
        )
      }
      this.stage = await createStage(
        canvas,
        {
          reducedMotion: this.reduced,
          lowPower: isLowPowerDevice(),
          preserveDrawingBuffer: import.meta.env.DEV && location.search.includes('timer-raf'),
        },
        () => this.disableStage(),
      )
    } catch (error) {
      console.warn('[stage] WebGL unavailable — falling back to a static backdrop.', error)
      this.disableStage()
      return
    }
    const state = store.get()
    this.stage.setPalette(state.cyber ? 'cyber' : state.theme, 0)
    this.stage.setAudience(state.audience)
    this.floorCount = this.stage.floorCount
    $('.readout__total').textContent = `/${pad(this.floorCount)}`
    this.resizeStage(true)
    canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault()
      this.disableStage()
    })
  }

  private disableStage(): void {
    this.stage = null
    this.root.classList.add('no-webgl')
    this.veil.style.opacity = '0'
  }

  private resizeStage(force = false): void {
    const canvas = $<HTMLCanvasElement>('#stage')
    const w = canvas.clientWidth || window.innerWidth
    const h = canvas.clientHeight || window.innerHeight
    // Mobile URL bars resize the viewport constantly while scrolling — ignore small height jitter.
    if (!force && coarsePointer && w === this.size.w && Math.abs(h - this.size.h) < 160) return
    this.size = { w, h }
    this.stage?.resize(w, h)
  }

  private observeLayout(): void {
    let queued = false
    const remeasure = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(() => {
        queued = false
        this.choreo.measure()
        this.scrollStop?.measure()
      })
    }
    new ResizeObserver(remeasure).observe($('main'))
    window.addEventListener('resize', () => {
      this.resizeStage()
      fitWordmark()
      remeasure()
    })
  }

  // ───────────────────────────── Frame loop ─────────────────────────────

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.frame)
  }

  private frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame)
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000))
    this.last = now

    this.scroller.raf(now)
    const y = this.scroller.y
    if (Math.abs(y - this.lastScrolledY) > 3) {
      this.lastScrolledY = y
      this.triggerInteractionPulse(true)
    }
    this.header.update(y)

    const layout = layoutViewport()
    const { shot, process } = this.choreo.compute(y, layout.w / layout.h)
    this.updateProcess(process)

    if (this.stage) {
      const veil = damp(this.veilValue < 0 ? shot.veil : this.veilValue, shot.veil, 4, dt)
      if (Math.abs(veil - this.veilValue) > 0.001) {
        this.veilValue = veil
        this.veil.style.opacity = veil.toFixed(3)
      }
      const p = this.pointer
      p.x = damp(p.x, p.tx, 2.5, dt)
      p.y = damp(p.y, p.ty, 2.5, dt)
      this.stage.render(now / 1000, dt, { shot, process, pointerX: p.x, pointerY: p.y })
    }
  }

  /** Steps highlight + "build progress" readout, driven by the same progress as the tower lights. */
  private updateProcess(progress: number): void {
    if (Math.abs(progress - this.lastProcess) < 0.0005) return
    this.lastProcess = progress
    this.stepsWrap.style.setProperty('--p', progress.toFixed(4))
    this.agent.setProgress(progress)

    const steps = this.stepsWrap.querySelectorAll<HTMLElement>('.step')
    const active = progress <= 0.02 ? -1 : Math.min(steps.length - 1, Math.floor(progress * steps.length))
    if (active !== this.activeStep) {
      this.activeStep = active
      steps.forEach((step, i) => {
        step.classList.toggle('is-active', i <= active)
        step.classList.toggle('is-current', i === active)
      })
    }

    const floor = Math.round(progress * this.floorCount)
    if (floor !== this.lastFloor) {
      this.lastFloor = floor
      this.readoutFloor.textContent = pad(floor)
      this.readoutFloor.parentElement!.parentElement!.style.setProperty('--p', String(floor / this.floorCount))
    }
  }
}
